import React from 'react';
import Label from '../../components/form/Label';

export const EXPLICIT_ONLY_TABLE_TYPE_OPTIONS = [
  { value: 'default', label: 'Regular' },
  { value: 'hightop', label: 'High-top' },
  { value: 'counter', label: 'Counter' },
  { value: 'bar', label: 'Bar' },
  { value: 'outdoor', label: 'Outdoor' },
] as const;

const TABLE_TYPE_ALIASES: Record<string, string> = {
  regular: 'default',
  standard: 'default',
};

const EXPLICIT_ONLY_TABLE_TYPES = new Set<string>(
  EXPLICIT_ONLY_TABLE_TYPE_OPTIONS.map((option) => option.value),
);

const canonicalizeTableType = (value: string): string | null => {
  const normalized = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '');
  const canonical = TABLE_TYPE_ALIASES[normalized] || normalized;
  return EXPLICIT_ONLY_TABLE_TYPES.has(canonical) ? canonical : null;
};

export const normalizeExplicitOnlyTableTypes = (values: unknown): string[] => {
  if (!Array.isArray(values)) return [];
  const seen = new Set<string>();
  const cleaned: string[] = [];
  values.forEach((value) => {
    if (typeof value !== 'string') return;
    const canonical = canonicalizeTableType(value);
    if (!canonical || seen.has(canonical)) return;
    seen.add(canonical);
    cleaned.push(canonical);
  });
  return cleaned;
};

interface ExplicitOnlyTableTypesFieldProps {
  value: unknown;
  onChange: (_values: string[]) => void;
}

const ExplicitOnlyTableTypesField: React.FC<
  ExplicitOnlyTableTypesFieldProps
> = ({ value, onChange }) => {
  const selectedTypes = normalizeExplicitOnlyTableTypes(value);

  return (
    <div className="mt-5 border-t border-gray-200 pt-5 dark:border-gray-700">
      <Label>Call out only-available seating</Label>
      <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
        When the only OpenTable availability at a time is one of these types,
        the agent will say so. Leave all unchecked to disable.
      </p>
      <div className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-5">
        {EXPLICIT_ONLY_TABLE_TYPE_OPTIONS.map(({ value: tableType, label }) => (
          <label
            key={tableType}
            className="flex items-start gap-3 rounded-lg py-1 text-sm"
          >
            <input
              type="checkbox"
              checked={selectedTypes.includes(tableType)}
              onChange={(event) => {
                const next = event.target.checked
                  ? [...selectedTypes, tableType]
                  : selectedTypes.filter((item) => item !== tableType);
                onChange(normalizeExplicitOnlyTableTypes(next));
              }}
              className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
            />
            <span className="leading-5 text-gray-800 dark:text-gray-100">
              {label}
            </span>
          </label>
        ))}
      </div>
    </div>
  );
};

export default ExplicitOnlyTableTypesField;
